import { useQuery } from "@tanstack/react-query";
import { useAxios } from "@/app/providers/AxiosProvider";
import handleError from "@lib/helpers/handleError";

const UUID = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i;

/** Whether `ref` is something GET /contacts/{contact} accepts: a uuid or a numeric id. */
export function isContactRef(ref: string): boolean {
  return /^\d+$/.test(ref) || UUID.test(ref);
}

/**
 * Loads a single contact by uuid (or legacy numeric id), for opening the
 * contact sheet from a deep link (`/contacts?contact=<uuid>`) when the row
 * isn't in the current page of results.
 *
 * Anything else in the URL is ignored rather than sent off to 404.
 */
export default function useContactDetails(
  ref: string | null,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const api = useAxios();
  const validRef = ref !== null && isContactRef(ref);

  // Keyed under "contacts" so useUpdateContact's invalidation refreshes it.
  const { isPending, error, data } = useQuery({
    queryKey: ["contacts", "detail", ref],
    queryFn: () => api.get(`/contacts/${ref}`),
    enabled: enabled && validRef,
    // A stale or someone else's link 404s/403s; retrying won't change that.
    retry: (failureCount, err: { response?: { status?: number } }) => {
      const status = err.response?.status ?? 0;
      return !(status >= 400 && status < 500) && failureCount < 2;
    },
  });

  handleError(error);

  return {
    // Single resources come back unwrapped (JsonResource::withoutWrapping).
    contact: data?.data ?? null,
    contactIsLoading: enabled && validRef && isPending,
    error,
    isInvalidRef: ref !== null && !validRef,
  };
}
